import Box from '@mui/material/Box';
import { MouseEvent, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { find, map } from 'lodash-es';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import Empty from 'src/common/components/Empty';
import DashboardListPageLayout from 'src/common/components/dashboard/DashboardListPageLayout';
import ListFilterEmptyState from 'src/common/components/dashboard/ListFilterEmptyState';
import { ticketCardGridItemSx, ticketCardGridSize, ticketCardScrollContainerSx } from 'src/common/styles/ticketCardStyles';

import { useAuth } from 'src/auth/useAuth';
import { dashboardPrimaryActionButtonProps } from 'src/common/components/DashboardPageToolbar';
import TrackMapListFilterForm from 'src/common/components/list-filters/TrackMapListFilterForm';
import { LanguageKeys } from 'src/common/constants/languages';
import { filterTrackMaps } from 'src/helpers/filterTrackMaps';
import { IBaseTrackMap, ITrackData, ITrackMap } from 'src/interfaces/TrackMap';
import usePersistentView from 'src/hooks/usePersistentView';
import { useTrackMapListFilters } from 'src/hooks/useTrackMapListFilters';
import { EView } from 'src/interfaces/View';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';
import useTrackMapsStore from 'src/stores/trackMapsStore';
import TrackMapsCard from './components/TrackMapCard';
import TrackMapEditorModal from './modals/TrackMapEditorModal';
import TrackMapsTable from './tables/TrackMapsTable';

const TrackMaps = () => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();

  const {
    trackMaps,
    deleteTrackMap: doDeleteTrackMap,
    deleteAllTrackMaps: doDeleteAllTrackMaps,
    fetchTrackMaps: doFetchTrackMaps,
    isLoading,
    saveTrackMap: doSaveTrackMap,
  } = useTrackMapsStore(trackMapsState => trackMapsState);
  const { isSuperAdmin } = useAuth();
  const { distancesById } = useSystemOptionsStore(s => s);

  const [view, setView] = usePersistentView();
  const {
    query,
    distanceFilter,
    publishedFilter,
    order,
    orderByKey,
    setQuery,
    setDistanceFilter,
    setPublishedFilter,
    setOrder,
    setOrderByKey,
  } = useTrackMapListFilters();
  const [trackMapEditorModalOpen, setTrackMapEditorModalOpen] = useState(false);

  const filteredTrackMaps = useMemo(
    () =>
      filterTrackMaps(trackMaps, distancesById ?? {}, currentLang, {
        query,
        distanceFilter,
        publishedFilter,
        sortBy: orderByKey,
        sortOrder: order,
      }),
    [trackMaps, distancesById, currentLang, query, distanceFilter, publishedFilter, orderByKey, order],
  );
  const [selectedTrackMap, setSelectedTrackMap] = useState<ITrackMap | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);

  const handleOpenTrackMapEditorModal = (id?: string, e?: MouseEvent<unknown>) => {
    e && e.stopPropagation();
    const thisTrack = find(trackMaps, { id });
    setSelectedTrackMap(thisTrack);
    setTrackMapEditorModalOpen(true);
  };

  const handleCloseTrackMapsEditorModal = () => {
    setSelectedTrackMap(undefined);
    setTrackMapEditorModalOpen(false);
  };

  const handleDeleteAllTrackMaps = async (e?: MouseEvent<unknown>) => {
    e && e.stopPropagation();
    const { confirmed } = await confirm({
      title: t('system.trackMaps.deleteAllConfirm'),
      description: t('system.trackMaps.deleteDescription'),
      confirmationText: t('confirmProvider.confirmationText'),
      cancellationText: t('confirmProvider.cancellationText'),
    });
    if (!confirmed) {
      enqueueSnackbar(t('system.trackMaps.deleteErrorMessage'), {
        variant: 'error',
      });
      return;
    }
    await doDeleteAllTrackMaps()
      .then(() => {
        enqueueSnackbar(t('system.trackMaps.deleteSuccessfulMessage'), {
          variant: 'success',
        });
      })
      .catch(
        ({
          response: {
            data: { errorCode, message },
          },
        }) => {
          enqueueSnackbar(errorCode ? t(`trackMaps.errors.${errorCode}`) : message, {
            variant: 'error',
          });
        },
      );
  };

  const handleDeleteTrackMap = async (id: string, e?: MouseEvent<unknown>) => {
    e && e.stopPropagation();
    const { confirmed } = await confirm({
      title: t('system.trackMaps.deleteConfirm'),
      description: t('system.trackMaps.deleteDescription'),
      confirmationText: t('confirmProvider.confirmationText'),
      cancellationText: t('confirmProvider.cancellationText'),
    });
    if (!confirmed) {
      enqueueSnackbar(t('system.trackMaps.deleteErrorMessage'), {
        variant: 'error',
      });
      return;
    }
    await doDeleteTrackMap(id);

    enqueueSnackbar(t('system.trackMaps.deleteSuccessfulMessage'), {
      variant: 'success',
    });
  };

  const saveTrackMap = async (trackMap: ITrackData & IBaseTrackMap, id?: string, onSuccess?: () => Promise<void>) => {
    setIsSaving(true);
    try {
      await doSaveTrackMap(trackMap, id);
      doFetchTrackMaps();
      enqueueSnackbar(t('system.trackMaps.saveTrackMapSuccess'), {
        variant: 'success',
      });

      // Mark pending files as used after successful save
      if (onSuccess) {
        await onSuccess();
      }

      handleCloseTrackMapsEditorModal();
    } catch {
      enqueueSnackbar(t('system.trackMaps.errors.saveTrackMapError'), {
        variant: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <DashboardListPageLayout
        title={t('system.trackMaps.pageTitle')}
        isLoading={isLoading}
        headerActions={
          <>
            {isSuperAdmin && (
              <Tooltip title={t('system.trackMaps.deleteAllTrackMaps')}>
                <IconButton
                  onClick={handleDeleteAllTrackMaps}
                  color="error"
                  aria-label={t('system.trackMaps.deleteAllTrackMaps')}
                >
                  <DeleteForeverIcon />
                </IconButton>
              </Tooltip>
            )}
            <Button
              {...dashboardPrimaryActionButtonProps()}
              onClick={() => handleOpenTrackMapEditorModal()}
              startIcon={<AddIcon />}
            >
              {t('system.trackMaps.new')}
            </Button>
          </>
        }
        toolbar={
          <TrackMapListFilterForm
            query={query}
            setQuery={setQuery}
            distanceFilter={distanceFilter}
            setDistanceFilter={setDistanceFilter}
            publishedFilter={publishedFilter}
            setPublishedFilter={setPublishedFilter}
            order={order}
            orderByKey={orderByKey}
            setOrder={setOrder}
            setOrderByKey={setOrderByKey}
            view={view}
            onSetView={setView}
          />
        }
      >
        {!isLoading && trackMaps.length === 0 && (
          <Empty
            icon={<MapOutlinedIcon />}
            title={t('empty.trackMaps.title')}
            description={t('empty.trackMaps.description')}
            primaryAction={{
              label: t('empty.addNew'),
              onClick: () => handleOpenTrackMapEditorModal(),
            }}
          />
        )}
        {!isLoading && trackMaps.length > 0 && filteredTrackMaps.length === 0 && (
          <ListFilterEmptyState />
        )}
        {filteredTrackMaps.length > 0 && (
          <Box sx={ticketCardScrollContainerSx(view !== EView.table)}>
            {view === EView.grid && (
              <Grid container spacing={2} sx={{ overflow: 'visible' }}>
                {map(filteredTrackMaps, trackMap => (
                  <Grid size={ticketCardGridSize} key={trackMap.id} sx={ticketCardGridItemSx}>
                    <TrackMapsCard
                      trackMap={trackMap}
                      onDeleteTrackMap={handleDeleteTrackMap}
                      onEditTrackMap={handleOpenTrackMapEditorModal}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
            {view === EView.table && (
              <TrackMapsTable
                trackMaps={filteredTrackMaps}
                onDeleteTrackMap={handleDeleteTrackMap}
                onEditTrackMap={handleOpenTrackMapEditorModal}
              />
            )}
          </Box>
        )}
      </DashboardListPageLayout>
      {trackMapEditorModalOpen && (
        <TrackMapEditorModal
          id="track-map-editor"
          open={trackMapEditorModalOpen}
          handleClose={handleCloseTrackMapsEditorModal}
          handleSave={saveTrackMap}
          selectedTrackMap={selectedTrackMap}
          isSaving={isSaving}
        />
      )}
    </>
  );
};

export default TrackMaps;
